import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { InfoIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import type { EmailDateHintContent } from '../utils/emailDateHint';

const PANEL_WIDTH = 340;
const VIEWPORT_PADDING = 8;
const PANEL_GAP = 6;

function computePanelStyle(anchor: DOMRect): CSSProperties {
  const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);
  let left = anchor.right - width;
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - VIEWPORT_PADDING - width;
  }

  const spaceBelow = window.innerHeight - anchor.bottom - PANEL_GAP - VIEWPORT_PADDING;
  const spaceAbove = anchor.top - PANEL_GAP - VIEWPORT_PADDING;
  const placeAbove = spaceBelow < 220 && spaceAbove > spaceBelow;

  if (placeAbove) {
    return {
      position: 'fixed',
      left,
      width,
      top: anchor.top - PANEL_GAP,
      transform: 'translateY(-100%)',
      maxHeight: Math.min(420, Math.max(160, spaceAbove)),
      overflowY: 'auto',
    };
  }

  return {
    position: 'fixed',
    left,
    width,
    top: anchor.bottom + PANEL_GAP,
    maxHeight: Math.min(420, Math.max(160, spaceBelow)),
    overflowY: 'auto',
  };
}

interface EmailDateHintPopoverProps {
  content: EmailDateHintContent;
  primaryTime: string;
  compact?: boolean;
  className?: string;
  zoneSectionTitle?: string;
  mailboxSectionTitle?: string;
}

export const EmailDateHintPopover: React.FC<EmailDateHintPopoverProps> = ({
  content,
  primaryTime,
  compact = false,
  className,
  zoneSectionTitle = 'Sent time elsewhere',
  mailboxSectionTitle = 'Per mailbox',
}) => {
  const { colors } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const panelId = React.useId();

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    setPanelStyle({
      ...computePanelStyle(el.getBoundingClientRect()),
      ...zStyle(Z_INDEX.portaledDropdown),
    });
  }, []);

  const showPanel = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleHide = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 150);
  }, [clearCloseTimer]);

  const togglePanel = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const panelContent =
    open && panelStyle ? (
      <div
        ref={panelRef}
        id={panelId}
        role="tooltip"
        style={panelStyle}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
      >
        <div
          className={cn(
            'rounded-lg border shadow-lg ring-1 ring-black/5 dark:ring-white/10',
            colors.surface,
            colors.border,
          )}
        >
          <div className={cn('px-3 pt-3 pb-2 border-b', colors.border)}>
            <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
              {content.primaryLabel}
            </p>
            <p className={cn('text-sm font-semibold mt-0.5 tabular-nums', colors.text)}>{primaryTime}</p>
            <p className={cn('text-xs mt-1 leading-snug', colors.textSecondary)}>{content.primaryDescription}</p>
            <p className={cn('text-[11px] mt-1 leading-snug', colors.textMuted)}>{content.summary}</p>
          </div>

          {content.received ? (
            <div className={cn('px-3 py-2.5 border-b', colors.border, colors.surfaceHover)}>
              <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                Received into mailbox
              </p>
              <div className="flex items-baseline justify-between gap-3 mt-1">
                <span className={cn('text-xs font-medium', colors.text)}>Your display time</span>
                <span className={cn('text-xs tabular-nums whitespace-nowrap shrink-0 font-semibold', colors.text)}>
                  {content.received.time}
                </span>
              </div>
              <p className={cn('text-[10px] mt-0.5 leading-snug', colors.textMuted)}>
                {content.received.offset}
                {' · UTC '}
                {content.received.utcTime}
              </p>
              <div className="flex items-start gap-1.5 mt-1.5">
                <span
                  className={cn(
                    'mt-1 h-1.5 w-1.5 rounded-full shrink-0',
                    content.received.lag.tone === 'normal' && 'bg-green-500',
                    content.received.lag.tone === 'warning' && 'bg-amber-500',
                    content.received.lag.tone === 'neutral' && 'bg-gray-400 dark:bg-gray-500',
                  )}
                  aria-hidden
                />
                <p className={cn('text-[10px] leading-snug min-w-0', colors.textSecondary)}>
                  {content.received.lag.text}
                </p>
              </div>
            </div>
          ) : null}

          {content.zoneRows.length > 0 ? (
            <div className="px-3 pt-2 pb-1">
              <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                {zoneSectionTitle}
              </p>
            </div>
          ) : null}

          <ul className="px-3 pb-2 space-y-2.5">
            {content.zoneRows.map((row) => (
              <li key={row.id} className="min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className={cn('text-xs font-medium truncate', colors.text)}>{row.label}</span>
                  <span className={cn('text-xs tabular-nums whitespace-nowrap shrink-0', colors.textSecondary)}>
                    {row.time}
                  </span>
                </div>
                <p className={cn('text-[10px] mt-0.5 leading-snug', colors.textMuted)}>
                  {row.offset}
                  {' · '}
                  {row.description}
                </p>
              </li>
            ))}
          </ul>

          {content.mailboxRows && content.mailboxRows.length > 0 ? (
            <div className={cn('px-3 py-2.5 border-t', colors.border, colors.surfaceHover)}>
              <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                {mailboxSectionTitle}
              </p>
              <ul className="mt-2 space-y-2">
                {content.mailboxRows.map((row) => (
                  <li key={row.id} className="min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={cn('text-xs font-medium truncate', colors.text)}>{row.label}</span>
                      <span className={cn('text-xs tabular-nums whitespace-nowrap shrink-0', colors.textSecondary)}>
                        {row.time}
                      </span>
                    </div>
                    {row.description ? (
                      <p className={cn('text-[10px] mt-0.5 leading-snug', colors.textMuted)}>{row.description}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {(content.headerRaw || content.headerOffset) && (
            <div className={cn('px-3 py-2.5 border-t', colors.border, colors.surfaceHover)}>
              <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                Sender Date header
              </p>
              {content.headerRaw ? (
                <p className={cn('text-xs mt-1 font-mono leading-snug break-all', colors.textSecondary)}>
                  {content.headerRaw}
                </p>
              ) : null}
              {content.headerDescription ? (
                <p className={cn('text-[10px] mt-1.5 leading-snug', colors.textMuted)}>
                  {content.headerDescription}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full p-0.5 transition-colors',
          open
            ? cn(colors.primaryFg, 'opacity-100')
            : cn('opacity-60 hover:opacity-100', colors.textMuted, colors.interactiveHover),
          className,
        )}
        aria-label="Timezone and date details"
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
        onFocus={showPanel}
        onBlur={(event) => {
          const next = event.relatedTarget as Node | null;
          if (next && panelRef.current?.contains(next)) return;
          setOpen(false);
        }}
        onClick={togglePanel}
      >
        <InfoIcon className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      </button>
      {typeof document !== 'undefined' && panelContent ? createPortal(panelContent, document.body) : null}
    </>
  );
};
